import {
  createStore,
  createEvent,
  combine,
  Store,
  Event,
  split,
  launch
} from '@effector/effector'
import {h, data, list, node, spec} from '../h'
import {log} from '../runtime/console'
import {HighlightValue, Highlight} from '../highlight/Highlight'
import {runRuntime} from '../workingSet'
import {
  stores,
  events,
  independentEvents,
  effects,
  samples,
  ons,
  watches,
  forwards,
  primeUnits
} from '../runtime/units'
import {
  generateEventHighlight,
  generateStoreHighlight,
  generateEffectHighlight,
  generateWatchHighlight,
  generateSampleHighlight,
  generateOnHighlight,
  generateForwardHighlight
} from '../highlight/generateHighlight'
import {runtimeID} from '../runtime/runtimeID'
import {pendingEffects} from '../runtime/activeEffects'
import {dynamicQueue} from '../batch'
import {runtimeError} from '../vizion/unitList'

import {section} from './section'
import {Timeline} from './TimelineHTML'

export {addTimelineEntry} from './timeline'
import {USE_ANIMATE} from '../env'
import {registerTab, selectTab, Tabs} from '../navigation/tab'
import {Play} from './play'
import {Diagrams} from './Diagrams'
// import {Report} from './report'
import {remap} from '../storeField'

const INACTIVE_EFFECT_TIMEOUT = 400

export function AppGrid() {
  h('section', () => {
    data({section: 'appgrid'})
    Units()
    Center('diagrams', {
      // report: {
      //   title: 'Report',
      //   content: Report
      // },
      overview: {
        title: 'Overview',
        content() {
          Play()
          ActiveEffects()
          Console()
          Timeline()
        }
      },
      code: {
        title: runtimeError.map(error => {
          if (Object(error) === error && error.message)
            return `Error: ${error.message}`
          return 'Code'
        }),
        content: Code
      },
      diagrams: {
        title: 'Diagrams',
        content: () => Diagrams({ons, samples, primeUnits})
      }
    })
  })
}

function Center(
  selected: string,
  tabs: {
    [tabName: string]: {
      title: Store<string> | string
      content: () => void
    }
  }
) {
  if (!(selected in tabs)) {
    console.error(`no default tab with name "${selected}"`)
  }
  for (const tabName in tabs) {
    registerTab({
      name: tabName,
      title: tabs[tabName].title,
      content: tabs[tabName].content
    })
  }
  selectTab(selected)
  section('center', () => {
    Tabs(({selected, content, name}) => {
      h('section', () => {
        spec({
          data: {tabBody: true, tabBodySelected: selected, tabName: name},
          visible: selected
        })
        content()
      })
    })
  })
}

function Code() {
  function createHighlight<T>(list: Store<T[]>, highlighter) {
    return list.map(list =>
      list.reduce((acc, data) => [...acc, ...highlighter(data)], [])
    )
  }
  const highlightedStores = createHighlight(stores, generateStoreHighlight)
  const highlightedEvents = createHighlight(
    independentEvents,
    generateEventHighlight
  )
  const highlightedEffects = createHighlight(effects, generateEffectHighlight)
  const highlightedSamples = createHighlight(samples, generateSampleHighlight)
  const highlightedOns = createHighlight(ons, generateOnHighlight)
  const highlightedForwards = createHighlight(
    forwards,
    generateForwardHighlight
  )
  const highlightedWatchers = createHighlight(watches, generateWatchHighlight)

  const highlightedSeparator = createStore([
    {
      type: 'space',
      value: `\n`
    }
  ])

  const highlightedCode = combine(
    highlightedStores,
    highlightedEvents,
    highlightedEffects,
    highlightedOns,
    highlightedForwards,
    highlightedSamples,
    highlightedWatchers,
    highlightedSeparator,
    (stores, events, effects, ons, forwards, samples, watchers, sep) => [
      ...stores,
      ...sep,
      ...events,
      ...sep,
      ...effects,
      ...sep,
      ...ons,
      ...sep,
      ...samples,
      ...sep,
      ...forwards,
      ...sep,
      ...watchers
    ]
  )
  Highlight(highlightedCode)
}

function Units() {
  function UnitGroup<T extends {key: symbol; name: string}>(
    title: string,
    units: Store<T[]>
  ) {
    h('section', () => {
      data({unitsGroup: true})
      h('header', () => {
        data({unitsTitle: true})
        h('h5', {
          text: title
        })
      })
      //@ts-ignore
      list({source: units, key: 'key'}, ({store}) => {
        h('div', {
          data: {unitsItem: true},
          text: remap(store, 'name')
        })
      })
    })
  }
  section('units', () => {
    h('header', () => {
      data({appgridHeader: 'units'})
      h('h4', {
        text: 'Units'
      })
    })
    UnitGroup('Stores', stores)
    UnitGroup(
      'Events',
      events.map(events => events.filter(e => !e.node.meta.named))
    )
    UnitGroup('Effects', effects)
  })
}
function Console() {
  section('console', () => {
    h('header', () => {
      data({appgridHeader: 'console'})
      h('h4', {
        text: 'Log'
      })
    })
    list({source: log, key: 'consoleCallID', reverse: true}, ({store}) => {
      h('div', () => {
        data({consoleRow: true})
        list(remap(store, 'args'), ({store}) => {
          HighlightValue(store)
        })
      })
    })
  })
}

function ActiveEffects() {
  section('active-effects', () => {
    const removeInactive = createEvent<number[]>()
    const currentRuntimeEffects = combine(
      runtimeID,
      pendingEffects,
      (id, list) => list.filter(item => item.runtimeID === id)
    )
    const pendingEffectsWithDelay = createStore<
      Array<{
        active: boolean
        name: string
        callID: number
        params: any
      }>
    >([])
      .reset(runRuntime)
      .on(removeInactive, (list, callIDs) =>
        list.filter(item => !callIDs.includes(item.callID))
      )
      .on(currentRuntimeEffects, (list, upd) => {
        const toRemove = Array(list.length).fill(true)
        const results = []
        const ids = list.map(item => item.callID)
        const removedIDs: number[] = []
        for (let i = 0; i < upd.length; i++) {
          const item = upd[i]
          const index = ids.indexOf(item.callID)
          if (index === -1) {
            results.push({
              active: true,
              name: item.name,
              callID: item.callID,
              params: item.params
            })
          } else {
            results.push(list[index])
            toRemove[index] = false
          }
        }
        for (let i = 0; i < list.length; i++) {
          if (!toRemove[i]) continue
          const item = list[i]
          if (!item.active) {
            results.push(item)
          } else {
            removedIDs.push(item.callID)
            results.push({
              active: false,
              name: item.name,
              callID: item.callID,
              params: item.params
            })
          }
        }
        if (removedIDs.length > 0) {
          setTimeout(removeInactive, INACTIVE_EFFECT_TIMEOUT, removedIDs)
        }
        return results
      })
    const groupedEffects = pendingEffectsWithDelay.map(e => {
      const callGroupMap = new Map<
        string,
        {
          name: string
          pending: number
          active: number
        }
      >()
      for (let i = 0; i < e.length; i++) {
        const item = e[i]
        let record = callGroupMap.get(item.name)
        if (!record) {
          record = {
            name: item.name,
            pending: 0,
            active: 0
          }
          callGroupMap.set(item.name, record)
        }
        record.pending += 1
        if (item.active) record.active += 1
      }
      return [...callGroupMap.values()]
    })
    h('header', () => {
      data({appgridHeader: 'active-effects'})
      h('h4', {
        text: 'Active effects'
      })
    })
    list({source: groupedEffects, key: 'name'}, ({store}) => {
      // const {isResolved} = split(store, {
      //   isResolved: ({active}) => !active
      // })
      h('p', () => {
        spec({
          text: store.map(({name, active}) => `${name} (${active} active)`)
        })
        // if (USE_ANIMATE) {
        //   let animation: Animation
        //   node(node => {
        //     animation = node.animate([{opacity: 0}, {opacity: 1}], {
        //       direction: 'normal',
        //       duration: 300,
        //       easing: 'ease-in-out',
        //       fill: 'both'
        //     })
        //   })
        //   isResolved
        //     .map(item => ({anim: animation, item}))
        //     .watch(e => {
        //       launch({
        //         target: reverseAnimation.trigger,
        //         params: [e],
        //         defer: true
        //       })
        //     })
        // }
      })
    })
    // list({source: pendingEffectsWithDelay, key: 'callID'}, ({store}) => {
    //   const {isResolved} = split(store, {
    //     isResolved: ({active}) => !active
    //   })
    //   h('p', () => {
    //     spec({
    //       text: remap(store, 'name')
    //     })
    //     if (USE_ANIMATE) {
    //       let animation: Animation
    //       node(node => {
    //         animation = node.animate([{opacity: 0}, {opacity: 1}], {
    //           direction: 'normal',
    //           duration: 300,
    //           easing: 'ease-in-out',
    //           fill: 'both'
    //         })
    //       })
    //       isResolved
    //         .map(item => ({anim: animation, item}))
    //         .watch(e => {
    //           launch({
    //             target: reverseAnimation.trigger,
    //             params: [e],
    //             defer: true
    //           })
    //         })
    //     }
    //   })
    // })
  })
}

const reverseAnimation = dynamicQueue<
  {
    anim: Animation
    item: {
      active: boolean
      name: string
      callID: number
      params: any
    }
  },
  {
    anim: Animation
    item: {
      active: boolean
      name: string
      callID: number
      params: any
    }
  },
  any
>({
  mark: 'hide pending fx',
  flatten: anim => [anim],
  timeout: 150,
  batchWindow: 25,
  fn({anim, item}) {
    if (!item.active) return
    anim.reverse()
  }
})
